import React, { useCallback, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import UploadCsvCard from './components/UploadCsvCard';
import ProductSearch from './components/ProductSearch';
import InvoicePriceReview from './components/InvoicePriceReview';
import StagingBar from './components/StagingBar';
import BatchReviewModal from './components/BatchReviewModal';
import PriceExportHistory from './components/PriceExportHistory';

const PriceReviewPage = () => {
  const { currentOrgId, user } = useAuth();
  const [lastSummary, setLastSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [stagedItems, setStagedItems] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);

  const canImport = useMemo(() => !!user?.id && !!currentOrgId, [user?.id, currentOrgId]);
  const hasStaged = stagedItems.length > 0;

  const toggleStage = useCallback((id) => {
    if (!id) return;
    setStagedItems((prev) => (
      prev.includes(id)
        ? prev.filter((entry) => entry !== id)
        : [...prev, id]
    ));
  }, []);

  const hydrateStaged = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    setStagedItems((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, []);

  const handleRemoveStaged = useCallback((id) => {
    if (!id) return;
    setStagedItems((prev) => prev.filter((entry) => entry !== id));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className={`max-w-screen-2xl mx-auto px-6 py-8 space-y-6 ${hasStaged ? 'pb-24' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Price Change Workbench</h1>
              <p className="text-sm text-muted-foreground">Stage approved price changes, review the batch, and export for EPOS updates.</p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/price-review/rules"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-muted transition-smooth"
                title="Manage matching rules"
              >
                <span>Rules</span>
              </a>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="inline-flex rounded-lg border border-border bg-muted p-1">
              <button
                type="button"
                onClick={() => setActiveTab('active')}
                className={`px-3 py-1.5 text-sm rounded-md transition-smooth ${
                  activeTab === 'active'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Active Variances
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`px-3 py-1.5 text-sm rounded-md transition-smooth ${
                  activeTab === 'history'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Export History
              </button>
            </div>
          </div>

          {activeTab === 'active' ? (
            <>
              <UploadCsvCard
                orgId={currentOrgId}
                userId={user?.id}
                disabled={!canImport}
                onComplete={setLastSummary}
              />

              <ProductSearch />

              <InvoicePriceReview
                selectedIds={stagedItems}
                onToggleItem={toggleStage}
                onHydrateStaged={hydrateStaged}
              />
            </>
          ) : (
            <PriceExportHistory orgId={currentOrgId} />
          )}

          {activeTab === 'active' && lastSummary && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-medium mb-2">Last Import Summary</h3>
              <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{JSON.stringify(lastSummary, null, 2)}</pre>
            </div>
          )}
        </div>
      </main>
      <StagingBar
        count={stagedItems.length}
        onReview={() => setBatchOpen(true)}
        onClear={() => setStagedItems([])}
      />
      <BatchReviewModal
        isOpen={batchOpen}
        onClose={() => setBatchOpen(false)}
        stagedIds={stagedItems}
        orgId={currentOrgId}
        userId={user?.id}
        onRemoveItem={handleRemoveStaged}
        onComplete={() => {
          setBatchOpen(false);
          setStagedItems([]);
        }}
      />
    </div>
  );
};

export default PriceReviewPage;
